import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Shield } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import SEO from '@/components/SEO';
import SpecialismSection from '@/components/SpecialismSection';
import ComparisonSection from '@/components/ComparisonSection';
import ShopfrontBanner from '@/components/ShopfrontBanner';
import PractitionerBanner from '@/components/PractitionerBanner';
import TestimonialsSection from '@/components/TestimonialsSection';

const STUDIO_IMG = 'https://images.unsplash.com/photo-1630245783610-824bda5ec1c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxtaW5pbWFsJTIwd2VsbG5lc3MlMjBzdHVkaW8lMjBpbnRlcmlvciUyMG5ldXRyYWwlMjB0b25lc3xlbnwwfHx8fDE3NzU0NzM1OTB8MA&ixlib=rb-4.1.0&q=85';

const faqs = [
  {
    q: 'Do I need a shopfront to build a successful practice?',
    a: "Not every business needs a shopfront. For many health, wellbeing and beauty practitioners, clients come through recommendations, repeat appointments, social media and online bookings.\n\nA private studio gives those clients a professional place to visit, while you build your own brand and manage your own schedule. Being part of a neighbourhood means you can still build local relationships and become known in your area.\n\nThe right choice depends on how your business attracts clients. If you rely heavily on walk-ins, street visibility may matter more. If you mainly work by appointment — or are building towards that model — a private studio could be a good fit.\n\nAt your viewing, tell us how your business works and we'll explore whether the location and studio suit your needs."
  },
  {
    q: 'Do I need an existing client base?',
    a: "You don't need a full client base to get started, but many practitioners operate through appointments, referrals, and social media rather than walk-in traffic."
  },
  {
    q: 'Are there other practitioners in the building?',
    a: "Each studio is private and operated independently. Practitioners run their own businesses with their own clients.\n\nSome locations may include a mix of professionals, but each operates separately within their own space."
  },
  {
    q: 'Will I be competing with others in the building?',
    a: "Your business is built around your own clients, service, and reputation.\n\nStudios are designed to give you full control over your space and how you operate."
  },
  {
    q: "What's included in the studio?",
    a: "Each studio provides a private, professional space ready for you to run your business. Details vary by location."
  },
  {
    q: 'How long are the agreements?',
    a: "Agreement lengths and break options depend on the studio and offer. We'll explain the applicable terms before you commit."
  },
  {
    q: 'How quickly can I get started?',
    a: "Once you've viewed, reserved, and completed onboarding, you can get set up quickly."
  },
  {
    q: 'Can I move or upgrade later?',
    a: "Yes — as your business grows, we can help you find a space that suits your next stage."
  },
];

export default function ForPractitionersPage() {
  return (
    <div data-testid="for-practitioners-page">
      <SEO title="For Practitioners" description="Private studios for health, wellbeing and beauty practitioners. Your own space, clearly explained terms, ongoing support." path="/for-practitioners" />

      {/* ── HERO ── */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in">
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A1A1A] tracking-tight mb-6">
                Specialist studios.<br />Built around practitioners.
              </h1>
              <p className="text-base text-[#5C5C55] leading-relaxed mb-6">
                Go Freestyle specialises in private studio workspace for independent health, wellbeing and beauty practitioners. We provide the space and professional environment to help you build your business on your terms — with your own identity, clients, pricing and schedule.
              </p>
              <p className="text-sm text-[#5C5C55] mb-6">Go Freestyle Studios gives you:</p>
              <div className="space-y-3 mb-8">
                {['Your own private studio', 'A professional environment for your clients', 'Clearly explained agreement terms', 'A simple, structured setup process', 'Ongoing support when needed'].map((b, i) => (
                  <div key={i} className={`flex items-center gap-3 animate-fade-in stagger-${i + 1}`}>
                    <div className="w-6 h-6 rounded-full bg-[#E8EDE5] flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-[#3A4035]" strokeWidth={2} />
                    </div>
                    <p className="text-sm text-[#1A1A1A] font-medium">{b}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-[#5C5C55]/80 mb-4">View a studio. See how it works. Decide with confidence.</p>
              <Link to="/book-a-viewing" className="inline-flex items-center gap-2 bg-[#3A4035] text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#2B3027] transition-colors" data-testid="practitioners-cta">
                Book a viewing <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <p className="text-sm text-[#8A8B85] mt-4">No pressure. No obligation. Just come and see.</p>
              <p className="text-sm text-[#5C5C55]/70 mt-2">Studios are limited — each space is intentionally one of one.</p>
            </div>
            <div className="animate-fade-in stagger-2">
              <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
                <img src={STUDIO_IMG} alt="Wellness studio interior" className="w-full h-72 sm:h-96 object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── A SPACE FOR YOUR SPECIALISM ── */}
      <SpecialismSection />

      {/* ── SHOPFRONT BANNER ── */}
      <ShopfrontBanner />

      {/* ── SHOP VS STUDIO COMPARISON ── */}
      <ComparisonSection />

      {/* ── YOUR SPACE, YOUR BUSINESS ── */}
      <section className="py-16 sm:py-24 bg-white" data-testid="section-your-space">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="flex items-start gap-5">
            <div className="w-12 h-12 rounded-2xl bg-[#E8EDE5] flex items-center justify-center shrink-0 mt-1">
              <Shield className="w-5 h-5 text-[#3A4035]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-6">
                Your space, your business
              </h2>
              <div className="space-y-4">
                <p className="text-base text-[#5C5C55] leading-relaxed">
                  Each studio is private and independent.
                </p>
                <p className="text-base text-[#5C5C55] leading-relaxed">
                  You run your business your way, with your own clients, pricing, and schedule.
                </p>
                <p className="text-base text-[#5C5C55] leading-relaxed">
                  You are not sharing your space or competing within it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHO THIS WORKS BEST FOR ── */}
      <section className="py-16 sm:py-24" data-testid="section-who">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-8 text-center">
            Who this works best for
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto">
            {[
              'Practitioners with existing or growing client bases',
              'Those working by appointment',
              'Those who value flexibility and control',
              'Those looking for a manageable next step for their business',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#FAF9F6] rounded-xl p-4">
                <div className="w-5 h-5 rounded-full bg-[#E8EDE5] flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-[#3A4035]" strokeWidth={2} />
                </div>
                <p className="text-sm text-[#1A1A1A] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS (hidden until genuine quotes are supplied) ── */}
      <TestimonialsSection testimonials={null} />

      {/* ── BRAND BANNER ── */}
      <PractitionerBanner />

      {/* ── BOOK A VIEWING INVITATION ── */}
      <section className="py-16 sm:py-24 bg-white" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <p className="text-sm text-[#5C5C55]/80 mb-4">View a studio. See how it works. Decide with confidence.</p>
          <Link to="/book-a-viewing" className="inline-flex items-center gap-2 bg-[#3A4035] text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#2B3027] transition-colors" data-testid="who-cta">
            Book a viewing <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <p className="text-sm text-[#8A8B85] mt-4">No pressure. No obligation. Just come and see.</p>
          <p className="text-sm text-[#5C5C55]/70 mt-2">Studios are limited — each space is intentionally one of one.</p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 sm:py-24" data-testid="faq-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-8 text-center">
            Common questions
          </h2>
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-0" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="bg-[#FAF9F6] rounded-xl px-5 py-4 text-sm font-medium text-[#1A1A1A] hover:bg-[#F2F0EB] hover:no-underline transition-colors text-left [&[data-state=open]]:rounded-b-none [&[data-state=open]]:bg-[#F2F0EB]">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="bg-[#F2F0EB] rounded-b-xl px-5 pb-4 pt-0">
                  {faq.a.split('\n\n').map((para, j) => (
                    <p key={j} className="text-sm text-[#5C5C55] leading-relaxed mt-2 first:mt-0">{para}</p>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-10">
            <p className="text-sm text-[#8A8B85] mb-4">Still have questions?</p>
            <a href="mailto:hello@gofreestyle.co.uk" className="inline-flex items-center gap-2 border border-[#E5E2DC] text-[#3A4035] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#F2F0EB] transition-colors" data-testid="faq-cta">
              Get in touch <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
